import React, { useEffect, useState, useRef } from 'react';
import { useDashboardContext } from '../hooks/useDashboardContext';

/**
 * 자동화된 대시보드 컴포넌트 생성기
 * LLM이 생성한 컴포넌트 템플릿을 받아서 자동으로 대시보드에 추가하고 편집 모드를 활성화합니다.
 * 편집 모달은 팝업하지 않고 "편집 버튼 누르고 차트 추가"한 것과 동일한 상태가 됩니다.
 */
const DashboardComponentCreator = ({ 
  componentTemplate, 
  autoAdd = true, 
  onComponentAdded 
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [error, setError] = useState(null);
  const { dashboardActions, editModeActions, dashboardTemplate, hasTemplate } = useDashboardContext();
  
  // 로컬 처리 상태 추적 (중복 호출 방지용)
  const processingRef = useRef(false);
  const processedRef = useRef(false);

  // 컴포넌트 템플릿이 변경될 때만 처리하도록 수정
  useEffect(() => {
    console.log('[DashboardComponentCreator] useEffect 실행:', {
      autoAdd,
      componentTemplateId: componentTemplate?.id,
      hasComponentTemplate: !!componentTemplate,
      isCurrentlyProcessing: processingRef.current,
      isProcessed: processedRef.current,
      isProcessing,
      isCompleted
    });

    if (autoAdd && componentTemplate && componentTemplate.id && 
        !processedRef.current && !processingRef.current && !isProcessing && !isCompleted) {
      
      console.log('[DashboardComponentCreator] 컴포넌트 처리 시작 조건 만족:', componentTemplate.id);
      
      // 즉시 처리 중 상태로 설정하여 중복 호출 방지
      processingRef.current = true;
      processedRef.current = true;
      processComponentAddition();
    } else {
      console.log('[DashboardComponentCreator] 컴포넌트 처리 건너뜀:', {
        reason: !autoAdd ? 'autoAdd false' :
                !componentTemplate ? 'no componentTemplate' :
                !componentTemplate.id ? 'no componentTemplate.id' :
                processedRef.current ? 'already processed' :
                processingRef.current ? 'currently processing (ref)' :
                isProcessing ? 'currently processing (state)' :
                isCompleted ? 'already completed' : 'unknown'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentTemplate?.id, autoAdd]); // 의존성을 componentTemplate.id로 변경

  const processComponentAddition = async () => {
    setIsProcessing(true);
    setError(null);
    
    try {
      console.log('[DashboardComponentCreator] 컴포넌트 자동 추가 시작:', componentTemplate);
      console.log('[DashboardComponentCreator] 컨텍스트 상태 확인:', {
        dashboardActions: !!dashboardActions,
        editModeActions: !!editModeActions,
        dashboardTemplate: !!dashboardTemplate,
        hasTemplate: hasTemplate,
        dashboardTemplateId: dashboardTemplate?.dashboard_id || dashboardTemplate?.dashboardId || 'null'
      });
      
      if (!dashboardActions) {
        console.warn('[DashboardComponentCreator] 대시보드가 현재 활성화되지 않았습니다.');
        setError('대시보드가 현재 활성화되지 않았습니다. 대시보드 페이지에서 다시 시도해주세요.');
        return;
      }

      if (!hasTemplate || !dashboardTemplate) {
        console.warn('[DashboardComponentCreator] 대시보드 템플릿이 로드되지 않았습니다.');
        setError('대시보드 템플릿이 로드되지 않았습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      // 컴포넌트를 로컬 상태에 추가 (백업 및 편집 모드 활성화는 내부에서 처리)
      console.log('[DashboardComponentCreator] LLM 템플릿으로 컴포넌트 로컬 추가 실행');
      const addedComponent = dashboardActions.addComponentToLocalState(componentTemplate);

      setIsCompleted(true);
      onComponentAdded?.(addedComponent);
      console.log('[DashboardComponentCreator] 컴포넌트 로컬 추가 완료 (편집 모드 활성화됨, 백엔드 저장 없음)');
      
    } catch (error) {
      console.error('[DashboardComponentCreator] 컴포넌트 자동 추가 실패:', error);
      setError(`컴포넌트 추가 실패: ${error.message || '알 수 없는 오류'}`);
      // 실패한 경우 재시도 가능하게 함
      processedRef.current = false;
    } finally {
      setIsProcessing(false);
      processingRef.current = false;
    }
  };

  // 컴포넌트가 이미 처리된 경우 아무것도 렌더링하지 않음
  if (processedRef.current && isCompleted) {
    return null;
  }

  if (error) {
    return (
      <div className="dashboard-component-creation-notification error">
        <span className="error-icon">❌</span>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="dashboard-component-creation-notification">
      {isProcessing && (
        <div className="creation-progress">
          <span className="spinner">⏳</span>
          <span>컴포넌트를 대시보드에 추가하는 중...</span>
        </div>
      )}
      
      {isCompleted && (
        <div className="creation-success">
          <span className="success-icon">✅</span>
          <span>컴포넌트 "{componentTemplate.title}"가 대시보드에 추가되었습니다.</span>
          <span className="edit-hint"> 편집 모드가 활성화되었습니다.</span>
        </div>
      )}
    </div>
  );
};

export default DashboardComponentCreator; 